package com.appsyde.shared.presentation.style

import org.jetbrains.compose.web.css.*

object AppStylesLoading : StyleSheet() {

    // ------------------------------------------------------------
    // Theme (calm / light) — aligned with landing
    // ------------------------------------------------------------
    private const val BG = "#F7F8FC"
    private const val TEXT = "#0F172A"
    private const val MUTED = "rgba(15,23,42,0.72)"
    private const val MUTED2 = "rgba(15,23,42,0.55)"
    private const val BORDER_SOFT = "rgba(15,23,42,0.10)"
    private const val SURFACE = "rgba(255,255,255,0.78)"
    private const val BRAND_VIOLET = "rgba(102,96,230,0.86)"
    private const val BRAND_CYAN = "rgba(96,188,217,0.82)"

    // Barre : glissement doux et continu (indeterminate)
    private val progressSlideKeyframes by keyframes {
        from { property("transform", "translate3d(-100%,0,0)") }
        to { property("transform", "translate3d(220%,0,0)") }
    }

    // Shimmer interne : bande de lumière qui traverse la barre
    private val progressShimmerKeyframes by keyframes {
        from { property("transform", "translateX(-100%)") }
        to { property("transform", "translateX(200%)") }
    }

    val page by style {
        property(
            "font-family",
            "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
        )
        property("background", BG)
        property("color", TEXT)
        property("-webkit-font-smoothing", "antialiased")
        property("min-height", "100vh")
        property("overflow", "hidden")
    }

    // Top bar (mini, juste pour cohérence)
    val topBar by style {
        height(56.px)
        property("display", "flex")
        alignItems(AlignItems.Center)
        paddingLeft(22.px)
        paddingRight(22.px)
        property("border-bottom", "1px solid $BORDER_SOFT")
        property("background", "rgba(247,248,252,0.72)")
        property("backdrop-filter", "blur(10px)")
    }

    val brand by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        fontSize(16.px)
        fontWeight(650)
        letterSpacing((-0.2).px)
        property("color", TEXT)
    }

    val brandMark by style {
        width(28.px)
        height(28.px)
        borderRadius(10.px)
    }

    /** Icône AppSyde dans la top bar (écran de connexion / chargement) */
    val brandIcon by style {
        height(16.px)
        property("width", "auto")
        property("max-height", "16px")
        property("object-fit", "contain")
        property("flex-shrink", "0")
        property("display", "block")
        borderRadius(4.px)
    }

    // Center layout
    val centerWrap by style {
        position(Position.Relative)
        property("min-height", "calc(100vh - 56px)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(22.px)
    }

    // Aurora like landing (very subtle, calm)
    val aurora by style {
        position(Position.Absolute)
        property("inset", "0")
        property("pointer-events", "none")
        opacity(0.55)
    }

    val blob1 by style {
        position(Position.Absolute)
        top((-240).px)
        left((-240).px)
        width(720.px)
        height(720.px)
        borderRadius(50.percent)
        property("background", "radial-gradient(circle, rgba(102,96,230,0.16), transparent 70%)")
        property("filter", "blur(28px)")
        opacity(0.95)
    }

    val blob2 by style {
        position(Position.Absolute)
        bottom((-260).px)
        right((-260).px)
        width(760.px)
        height(760.px)
        borderRadius(50.percent)
        property("background", "radial-gradient(circle, rgba(96,188,217,0.12), transparent 70%)")
        property("filter", "blur(30px)")
        opacity(0.95)
    }

    // The card (paper-like)
    val card by style {
        width(100.percent)
        maxWidth(760.px)
        borderRadius(24.px)
        property("border", "1px solid $BORDER_SOFT")
        property("background", SURFACE)
        paddingTop(30.px)
        paddingBottom(30.px)
        paddingLeft(38.px)
        paddingRight(38.px)
        property("box-shadow", "0 18px 52px rgba(15,23,42,0.10)")
        property("backdrop-filter", "blur(10px)")
        textAlign("center")
    }

    val kicker by style {
        marginBottom(10.px)
        marginTop(0.px)
        marginLeft(0.px)
        marginRight(0.px)
        fontSize(13.px)
        lineHeight("1.2")
        fontWeight(620)
        letterSpacing(0.2.px)
        property("text-transform", "none")
        property("color", "rgba(15,23,42,0.55)")
        property("text-shadow", "none")
    }

    val gradientWord by style {
        property("background", "linear-gradient(135deg, rgba(102,96,230,0.95), rgba(96,188,217,0.85))")
        property("-webkit-background-clip", "text")
        property("-webkit-text-fill-color", "transparent")
        property("background-clip", "text")
        property("color", "transparent")
    }

    val phaseHeadline by style {
        marginTop(0.px)
        marginBottom(12.px)
        property("margin-left", "auto")
        property("margin-right", "auto")
        maxWidth(620.px)
        fontSize(32.px)
        lineHeight("1.16")
        fontWeight(670)
        letterSpacing((-0.7).px)
        property("color", "rgba(31,42,68,0.94)")
        property("min-height", "90px")
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("text-wrap", "balance")
        property("transition", "opacity 260ms ease, transform 260ms ease")
        media(mediaMaxWidth(860.px)) {
            self style {
                fontSize(28.px)
                property("min-height", "78px")
            }
        }
        media(mediaMaxWidth(520.px)) {
            self style {
                fontSize(22.px)
                lineHeight("1.2")
                property("min-height", "72px")
                maxWidth(100.percent)
            }
        }
    }

    val subtitle by style {
        marginTop(0.px)
        marginBottom(18.px)
        fontSize(15.px)
        lineHeight("1.5")
        property("color", "rgba(31,42,68,0.62)")
        fontWeight(470)
    }

    val phaseVisible by style {
        opacity(1)
        property("transform", "translateY(0)")
    }

    val phaseHidden by style {
        opacity(0)
        property("transform", "translateY(6px)")
    }

    /** Track : plus fin, plus élégant. */
    val progressTrack by style {
        width(390.px)
        height(4.px)
        borderRadius(999.px)
        property("background", "rgba(102,96,230,0.06)")
        property("border", "none")
        property("overflow", "hidden")
        property("margin", "0 auto")
        position(Position.Relative)
        media(mediaMaxWidth(520.px)) {
            self style { width(100.percent) }
        }
    }

    /** Barre : gradient subtil + glissement fluide + shimmer interne. */
    val progressIndicator by style {
        height(100.percent)
        width(38.percent)
        borderRadius(999.px)
        property(
            "background",
            "linear-gradient(90deg, rgba(102,96,230,0.52), rgba(102,96,230,0.38), rgba(96,188,217,0.42))"
        )
        property("box-shadow", "none")
        property("transform", "translate3d(-100%,0,0)")
        property("will-change", "transform")
        animation(progressSlideKeyframes) {
            duration(2200.ms)
            timingFunction(AnimationTimingFunction.EaseInOut)
            iterationCount(100)
        }
        property("position", "relative")
        property("overflow", "hidden")
        property("isolation", "isolate")
        self + before style {
            property("content", "\"\"")
            property("position", "absolute")
            property("top", "0")
            property("bottom", "0")
            property("left", "0")
            property("width", "60%")
            property(
                "background",
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.28), rgba(255,255,255,0.5), rgba(255,255,255,0.28), transparent)"
            )
            animation(progressShimmerKeyframes) {
                duration(1800.ms)
                timingFunction(AnimationTimingFunction.EaseInOut)
                iterationCount(100)
            }
        }
    }

    val footer by style {
        position(Position.Absolute)
        bottom(18.px)
        left(0.px)
        right(0.px)
        textAlign("center")
        fontSize(12.px)
        property("color", MUTED2)
    }
}
